import { useEffect, useMemo } from 'react';
import { useRouteMatch } from 'react-router-dom';

import useServiceCategoriesQuery from '@/modules/employer/queries/useServiceCategories.query';
import { Plan } from '@/modules/plan/plan.types.ts';
import { usePlansStore } from '@/modules/Renewals/RenewalSetup/stores';

import { useGetRenewalSetupByPlanYearIdQuery } from '../queries';
import useCopayStore from '../stores/useCopay.store';
import useHealthPlansStore from '../stores/useHealthPlans.store';
import usePayrollCalendarsStore from '../stores/usePayrollCalendars.store';
import { getFormattedRenewalSetupData } from '../utils';

const useCurrentRenewalSetup = (externalId?: string) => {
  const {
    params: { id },
  } = useRouteMatch<{ id: string }>();
  const { list: serviceCategories } = useServiceCategoriesQuery();
  const handleSetCopaysState = useCopayStore((state) => state.setState);
  const handleSetPlansState = usePlansStore((state) => state.setState);
  const handleSetHealthPlansState = useHealthPlansStore((state) => state.setState);
  const handleSetPayrollCalendarsState = usePayrollCalendarsStore((state) => state.setState);
  const { data, isSuccess, refetch, isLoading, isError } = useGetRenewalSetupByPlanYearIdQuery(
    externalId || id
  );

  const formattedData = useMemo(
    () => getFormattedRenewalSetupData(data, serviceCategories),
    [data, serviceCategories]
  );

  useEffect(() => {
    if (formattedData?.copays) {
      handleSetCopaysState({ copays: formattedData.copays });
    }
    if (formattedData?.plans?.length) {
      handleSetPlansState({
        plans: formattedData.plans.map((plan: Plan) => ({
          id: plan.id,
          planCode: plan.planCode || '',
          planName: plan.name,
          defaultPlanCode: plan.planCode || '',
          defaultPlanName: plan.name,
        })),
      });
    }
  }, [
    formattedData?.copays,
    formattedData?.plans,
    formattedData?.plans?.length,
    handleSetCopaysState,
    handleSetPlansState,
  ]);

  useEffect(() => {
    if (formattedData?.healthPlans?.length) {
      handleSetHealthPlansState({ healthPlans: formattedData.healthPlans });
    }
  }, [formattedData?.healthPlans, handleSetHealthPlansState]);

  useEffect(() => {
    if (formattedData?.payrollCalendars?.length) {
      handleSetPayrollCalendarsState({ payrollCalendars: formattedData.payrollCalendars });
    }
  }, [formattedData?.payrollCalendars, handleSetPayrollCalendarsState]);

  return {
    renewalSetupData: formattedData,
    serviceCategories,
    isSuccess,
    isError,
    isLoading,
    refetch,
  };
};

export default useCurrentRenewalSetup;
